/**
 * The shared browser tracker.
 *
 * Passive, anonymous navigation telemetry over OTLP/HTTP: every tracked action becomes an OTel LOG
 * RECORD and, for navigations and UI events, a short span. Only opaque random ids and a coarse device
 * class — never PII, never a URL, never element content.
 *
 * ── Safety contract, test-enforced in both consumers ────────────────────────────────────────────
 *
 * Telemetry must NEVER throw or block the page. SDK init and every emit are wrapped; a down or
 * unreachable collector silently no-ops because the OTLP batch exporters are async and swallow
 * failures; OTel's no-op diag logger is left in place. `initialised` is set BEFORE the try block so a
 * known-bad setup is not retried on every emit.
 *
 * ── Why the span is short ───────────────────────────────────────────────────────────────────────
 *
 * `page_view` stays a log record plus a zero-duration span rather than becoming a navigation-duration
 * span. React Router's data router renders asynchronously across effects, so a span held open across
 * them would frequently never be ended. The correct way to see navigation cost is the `fetch` spans
 * the route triggers, joined by `journey.id` (§10).
 */
import { context, propagation, trace, ROOT_CONTEXT, type Span, type Tracer } from '@opentelemetry/api'
import { logs, SeverityNumber, type Logger } from '@opentelemetry/api-logs'
import { W3CBaggagePropagator, W3CTraceContextPropagator, CompositePropagator } from '@opentelemetry/core'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions'
import { BatchSpanProcessor, StackContextManager, WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import {
  ATTR,
  clearAllJourneys,
  encodeJourneyBaggage,
  endJourney as endJourneyIn,
  readJourney,
  sanitiseAttributes,
  toMutableAttributes,
  startJourney as startJourneyIn,
  type AttrValue,
  type MutableAttrValue,
  type BootstrapStage,
  type DeviceClass,
  type Journey,
  type JourneyName,
} from '@terramantle/telemetry'

export interface TrackerConfig {
  /** `service.name`, the one attribute that separates the two front-ends in the collector. */
  serviceName: string
  /**
   * Paths from which NOTHING is emitted. The portal's `/callback` carries the OIDC `?code=`.
   * `trackError` is the single documented exception — see its doc comment.
   */
  excludePaths?: string[]
  /**
   * Install `<meta name="referrer" content="no-referrer">` at init.
   *
   * Still needed, though the original reason is now stale. The exporter no longer uses
   * `sendBeacon`/XHR — `@opentelemetry/otlp-exporter-base` deprecated that path in favour of `fetch`
   * with `keepalive`, and `fetch` *does* accept a `referrerPolicy`. But the OTLP exporter exposes no
   * way to set one (its config is url/headers/timeout/concurrency), so a page-wide meta remains the
   * only control. Without it the full current URL — including a `/callback ?code` — rides along as a
   * `Referer` header.
   */
  installNoReferrer?: boolean
  /** Origins that receive `traceparent`/`baggage`. Scoped tightly: only the registry API. */
  propagateTo?: RegExp[]
  /**
   * The build-time env bag, passed IN by the consumer rather than read from `import.meta.env` here.
   *
   * Deliberate: a shared package should not reach for its host build tool's ambient globals. It also
   * fixes a real problem - when this package is a workspace dependency, Vitest may treat it as
   * external and not rewrite `import.meta.env` inside it, so `vi.stubEnv` in a consumer's test would
   * silently not reach this code and the env-dependent tests would fail for no visible reason.
   */
  env: Record<string, string | undefined>
}

export interface Tracker {
  sessionId(): string
  deviceId(): string
  /**
   * Set (or clear with `null`) the signed-in user, so browser records carry `user.id`/`user.email`.
   * Held in memory only - call it after auth resolves on each page load. See the implementation note.
   */
  setUser(u: { id?: string; email?: string } | null): void
  captureCampaign(): string | undefined
  resetTelemetryIdentity(): void
  /**
   * One navigation. `route` is the matched route TEMPLATE (`/org/:orgSlug/modules`), which the host
   * derives from its router config - this package deliberately knows nothing about react-router. It
   * is remembered as the current route, so every later event on this page carries it too.
   */
  trackPageView(path: string, fromPath?: string, route?: string): void
  trackEvent(type: string, target?: string, opts?: { from?: string }): void
  trackHealthcheck(): void
  trackError(stage: BootstrapStage | string, err?: unknown, opts?: { fromExcludedPath?: boolean }): void
  trackTiming(name: string, durationMs: number, attributes?: Record<string, AttrValue | undefined>): void
  /** Flush queued spans and logs immediately, fire and forget. Bound to page-hide automatically. */
  flush(): void
  startJourney(name: JourneyName): Journey
  endJourney(name: JourneyName): void
  currentJourney(name: JourneyName): Journey | null
  /** `baggage` header value for the live journey, or undefined. For api.ts's single request(). */
  currentJourneyBaggage(name?: JourneyName): string | undefined
  /** `traceparent`/`tracestate`/`baggage` to merge into an outbound request's headers. */
  traceHeaders(journeyName?: JourneyName): Record<string, string>
  __resetPathState(): void
  __resetTelemetryForTest(): Promise<void>
}

const MAX_VALUE_LENGTH = 512
const SID_KEY = 'tm_sid'
const UID_KEY = 'tm_uid'
const HC_KEY = 'tm_hc'
const CAMPAIGN_KEY = 'tm_campaign'

const uuid = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/** Storage access is DEFENSIVE by contract: incognito and blocked site-data must not throw. */
const readStored = (store: Storage | undefined, key: string): string | null => {
  try {
    return store?.getItem(key) ?? null
  } catch {
    return null
  }
}
const writeStored = (store: Storage | undefined, key: string, value: string): void => {
  try {
    store?.setItem(key, value)
  } catch {
    /* ignore */
  }
}
const removeStored = (store: Storage | undefined, key: string): void => {
  try {
    store?.removeItem(key)
  } catch {
    /* ignore */
  }
}
const sessionStore = (): Storage | undefined => {
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}
const localStore = (): Storage | undefined => {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

/**
 * Coarse device class from viewport width plus touch capability.
 *
 * Deliberately NOT user-agent based. A closed three-value enum is enough to segment page views by
 * device and keeps telemetry free of fingerprinting surface — which is exactly why §4 excludes
 * `browser.brands` and `browser.platform`.
 */
function deviceClass(): DeviceClass {
  try {
    const width = window.innerWidth
    if (width < 768) return 'mobile'
    if (width < 1024) return typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0 ? 'tablet' : 'desktop'
    return 'desktop'
  } catch {
    return 'desktop'
  }
}

export function createTracker(cfg: TrackerConfig): Tracker {
  const env = cfg.env
  const serviceName = env['VITE_OTEL_SERVICE_NAME'] ?? cfg.serviceName
  const endpoint = (env['VITE_OTEL_EXPORTER_OTLP_ENDPOINT'] ?? 'https://otel.planesailing.io').replace(/\/+$/, '')
  /**
   * Ingest auth. This value ships in the PUBLIC bundle, so for browser RUM it is a noise filter and
   * not a secret — the collector's edge rate-limit is the real abuse control. Server-side producers
   * use a different receiver with a genuinely secret token, which is why the gateway's public
   * receiver now drops anything claiming a non-browser `service.name`.
   */
  const token = env['VITE_OTEL_INGEST_TOKEN']
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined
  const disabled = env['VITE_OTEL_DISABLED'] === 'true'
  const excluded = cfg.excludePaths ?? []
  const serviceVersion = env['VITE_GIT_SHA']
  const environment = env['VITE_ENVIRONMENT'] ?? 'production'

  let initialised = false
  let tracerProvider: WebTracerProvider | undefined
  let loggerProvider: LoggerProvider | undefined
  let onVisibilityChange: (() => void) | undefined
  let onPageHide: (() => void) | undefined

  // Shared path state so UI events carry the same journey context page views do. trackPageView is
  // the single writer. Initialised from the landing URL so a click firing before the first route
  // effect still reports a correct app.path.
  let currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
  let previousPath: string | undefined
  // The matched route template for `currentPath`. Not derivable here - it comes from the host's
  // router config via trackPageView - so it starts undefined and stays undefined on a surface that
  // has no routes (the SSG marketing pages) or for a path no route matched.
  let currentRoute: string | undefined

  const onExcludedPath = (): boolean => {
    try {
      return excluded.includes(window.location.pathname)
    } catch {
      return false
    }
  }

  function installNoReferrer(): void {
    try {
      if (typeof document === 'undefined') return
      if (document.querySelector('meta[name="referrer"]')) return
      const meta = document.createElement('meta')
      meta.setAttribute('name', 'referrer')
      meta.setAttribute('content', 'no-referrer')
      document.head.appendChild(meta)
    } catch {
      /* head unavailable */
    }
  }

  /**
   * Flush whatever is queued, fire and forget.
   *
   * Deliberately NOT awaited. During unload there is no opportunity to await anything, and the OTLP
   * exporter transports over `fetch` with `keepalive`, which browsers allow to outlive the page that
   * started it (within a 64KiB cumulative cap). So handing the request to the browser IS the delivery
   * mechanism; waiting for it would only be waiting for a page that is already gone.
   */
  function flushNow(): void {
    try {
      void tracerProvider?.forceFlush().catch(() => undefined)
    } catch {
      /* never let telemetry break the page, least of all on the way out */
    }
    try {
      void loggerProvider?.forceFlush().catch(() => undefined)
    } catch {
      /* ignore */
    }
  }

  /**
   * Get queued records out before the page stops running.
   *
   * ── The problem this solves ────────────────────────────────────────────────────────────────────
   *
   * `BatchLogRecordProcessor` and `BatchSpanProcessor` hold records in memory and flush on a 5s
   * timer. Nothing flushed before the page went away, with two consequences:
   *
   *  1. The last ≤5 seconds of EVERY session were lost — including the final click before someone
   *     navigates away, which is usually the most interesting one. Silent, and far larger than (2).
   *  2. When a tab is SUSPENDED (laptop sleep, mobile backgrounding) the timer stops firing. On
   *     resume the queue flushes carrying each record's ORIGINAL creation timestamp, now hours
   *     stale — and Loki rejects anything more than `max_chunk_age / 2` (1 hour) behind the
   *     stream's newest entry. Observed 17 times over 30 days, always from `terramantle-web`, which
   *     fits: the marketing site is what people leave open in a background tab.
   *
   * ── Why these two events and NOT `beforeunload` ───────────────────────────────────────────────
   *
   *  - `visibilitychange` → `hidden` is the reliable one, and the only one that fires before a
   *    mobile browser suspends a backgrounded tab. It is what addresses (2).
   *  - `pagehide` covers navigating away AND closing the tab or window, which is the "window
   *    closes" case. It fires in situations `unload` does not.
   *  - `beforeunload` is deliberately omitted. It is unreliable on mobile, it is redundant with
   *    `pagehide` here, and merely REGISTERING a listener for it disqualifies the page from the
   *    back/forward cache in some browsers — making back navigation measurably slower for every
   *    visitor. Paying a real UX cost for a duplicate signal is a bad trade.
   *
   * A `pagehide` with `persisted === true` means the page is being frozen into the back/forward
   * cache and may be restored. Flushing is still right — get the data out — and nothing is shut
   * down, so a restored page keeps working.
   */
  function installFlushOnHide(): void {
    try {
      if (typeof document === 'undefined' || typeof window === 'undefined') return
      onVisibilityChange = (): void => {
        // Only on the way out. 'visible' fires on every tab return and has nothing to flush.
        if (document.visibilityState === 'hidden') flushNow()
      }
      onPageHide = (): void => flushNow()
      document.addEventListener('visibilitychange', onVisibilityChange)
      window.addEventListener('pagehide', onPageHide)
    } catch {
      /* no document/window - the SSG prerender runs this module under Node */
    }
  }

  function ensureInit(): boolean {
    if (initialised) return Boolean(loggerProvider || tracerProvider)
    initialised = true
    try {
      if (cfg.installNoReferrer) installNoReferrer()
      installFlushOnHide()

      const resource = resourceFromAttributes({
        [ATTR_SERVICE_NAME]: serviceName,
        // §4 requires these; previously only service.name was set, so nothing could tell a staging
        // span from a production one or attribute a regression to a deploy.
        ...(serviceVersion ? { [ATTR.SERVICE_VERSION]: serviceVersion } : {}),
        [ATTR.SERVICE_NAMESPACE]: 'terramantle',
        [ATTR.DEPLOYMENT_ENVIRONMENT]: environment,
      })

      tracerProvider = new WebTracerProvider({
        resource,
        spanProcessors: [
          new BatchSpanProcessor(new OTLPTraceExporter({ url: `${endpoint}/v1/traces`, headers })),
        ],
      })
      trace.setGlobalTracerProvider(tracerProvider)

      loggerProvider = new LoggerProvider({
        resource,
        processors: [
          new BatchLogRecordProcessor({
            exporter: new OTLPLogExporter({ url: `${endpoint}/v1/logs`, headers }),
          }),
        ],
      })
      logs.setGlobalLoggerProvider(loggerProvider)

      // ── Propagation ────────────────────────────────────────────────────────────────────────────
      //
      // This is the change that alters the old design, which deliberately registered NO context
      // manager because it only emitted isolated spans. Propagation needs one.
      //
      // StackContextManager, not zone.js: no extra dependency, and it is honest about its limits —
      // context does NOT survive an await across React effects. That is precisely why `journey.id`
      // lives in explicit sessionStorage rather than in context (§11).
      context.setGlobalContextManager(new StackContextManager().enable())
      propagation.setGlobalPropagator(
        new CompositePropagator({ propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()] }),
      )

      registerInstrumentations({
        instrumentations: [
          // DNS/TCP/TTFB/DOM-complete for the SPA shell.
          new DocumentLoadInstrumentation(),
          new FetchInstrumentation({
            // Scoped TIGHTLY: only the registry API gets trace headers. A wildcard here would send
            // internal ids to every third party the page talks to.
            propagateTraceHeaderCorsUrls: cfg.propagateTo ?? [],
            // Without this the exporter traces its own exports, which is an infinite regress that
            // shows up as a flood of self-referential spans (§10).
            ignoreUrls: [new RegExp(endpoint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))],
            clearTimingResources: true,
          }),
        ],
      })

      return true
    } catch {
      // initialised stays true: do not retry a known-bad setup on every emit.
      return false
    }
  }

  const getLogger = (): Logger => logs.getLogger(serviceName)
  const getTracer = (): Tracer => trace.getTracer(serviceName)

  /**
   * The sanitiser, from the shared package.
   *
   * This replaces a local `cleanAttributes()` that each front-end had its own copy of. The shared one
   * is a superset — drop undefined/empty and truncate to 512, PLUS the §16.2 key denylist, URL query
   * stripping, and a type signature that makes an object attribute a compile error.
   */
  const clean = (attrs: Record<string, AttrValue | null | undefined>): Record<string, MutableAttrValue> =>
    toMutableAttributes(sanitiseAttributes(attrs))

  function emitLog(name: string, attributes: Record<string, MutableAttrValue>, severity = SeverityNumber.INFO): void {
    try {
      getLogger().emit({
        severityNumber: severity,
        severityText: severity >= SeverityNumber.ERROR ? 'ERROR' : 'INFO',
        body: name,
        attributes: { 'event.name': name, ...attributes },
      })
    } catch {
      /* never let telemetry break the page */
    }
  }

  function emitSpan(name: string, attributes: Record<string, MutableAttrValue>): void {
    try {
      const span: Span = getTracer().startSpan(name, { attributes }, ROOT_CONTEXT)
      span.end()
    } catch {
      /* never let telemetry break the page */
    }
  }

  // ── Identity ────────────────────────────────────────────────────────────────────────────────────

  const sessionId = (): string => {
    const store = sessionStore()
    let s = readStored(store, SID_KEY)
    if (!s) {
      s = uuid()
      writeStored(store, SID_KEY, s)
    }
    return s
  }

  const deviceId = (): string => {
    const store = localStore()
    let u = readStored(store, UID_KEY)
    if (!u) {
      u = uuid()
      writeStored(store, UID_KEY, u)
    }
    return u
  }

  function captureCampaign(): string | undefined {
    if (typeof window === 'undefined') return undefined
    const store = sessionStore()
    const existing = readStored(store, CAMPAIGN_KEY)
    if (existing) return existing
    try {
      const params = new URLSearchParams(window.location.search)
      const value = params.get('tm_campaign') ?? params.get('utm_campaign') ?? params.get('utm_source')
      if (value) {
        const trimmed = value.slice(0, MAX_VALUE_LENGTH)
        writeStored(store, CAMPAIGN_KEY, trimmed)
        return trimmed
      }
    } catch {
      /* malformed search string */
    }
    return undefined
  }

  /**
   * Clear the telemetry identity at the auth boundary (logout).
   *
   * Clears every `tm_journey_*` key as well as the ids. §7 requires that: a journey left behind at
   * logout would stitch two different users' activity into one query result on a shared device.
   *
   * This existed only in `packages/app` before; `terramantle.dev` had no copy at all, so the
   * marketing site would have had a privacy gap by omission the moment it grew auth.
   */
  /**
   * The signed-in user, for `user.id` / `user.email` on browser records.
   *
   * ── Why this is in MEMORY and never in storage ──────────────────────────────────────────────
   *
   * `tm_sid`/`tm_uid` are persisted because a session and a device must survive a reload. An email
   * must not: writing it to sessionStorage would leave personal data sitting in the browser long
   * after sign-out, readable by any script on the origin, for no benefit - the app re-establishes
   * who the user is on every load anyway, from the token it already has. So this resets to empty on
   * every page load and `setUser` is called again after auth resolves.
   *
   * ── What this attribute is and is not worth ────────────────────────────────────────────────
   *
   * §16's email ban was lifted by explicit decision and extended to the browser on request. Worth
   * being precise about the threat model, because it differs from the server side: this sends the
   * user's OWN email from their OWN browser, so it is not a cross-user leak. But the browser's OTLP
   * token ships in a public bundle, so ANYONE can post a span carrying an arbitrary `user.email`.
   * Treat a browser-reported email as a hint, never as evidence of who did something - the
   * server-side value, derived from a validated bearer token, is the authoritative one.
   */
  let user: { id?: string; email?: string } = {}

  function setUser(u: { id?: string; email?: string } | null): void {
    user = u ? { ...(u.id ? { id: u.id } : {}), ...(u.email ? { email: u.email } : {}) } : {}
  }

  /**
   * The identity every browser span and log record carries.
   *
   * One helper rather than repeating the pair at each emit site, which is how `user.id`/`user.email`
   * came to be missing from the server span for so long - mirrors `identityAttributes()` in the
   * Worker so the two sides name a person the same way.
   */
  function identityAttrs(): Record<string, AttrValue | undefined> {
    return {
      [ATTR.SESSION_ID]: sessionId(),
      [ATTR.DEVICE_ID]: deviceId(),
      ...(user.id ? { [ATTR.USER_ID]: user.id } : {}),
      ...(user.email ? { [ATTR.USER_EMAIL]: user.email } : {}),
    }
  }

  function resetTelemetryIdentity(): void {
    removeStored(sessionStore(), SID_KEY)
    removeStored(localStore(), UID_KEY)
    removeStored(sessionStore(), HC_KEY)
    clearAllJourneys(sessionStore())
    // Sign-out must drop the email too, or the next user on this device inherits it.
    setUser(null)
  }

  // ── Journeys (§7 case A) ────────────────────────────────────────────────────────────────────────

  const startJourney = (name: JourneyName): Journey => startJourneyIn(sessionStore(), name, uuid)
  const endJourney = (name: JourneyName): void => endJourneyIn(sessionStore(), name)
  const currentJourney = (name: JourneyName): Journey | null => readJourney(sessionStore(), name)

  /**
   * The live journey, preferring an explicitly named one.
   *
   * Without a name it scans the browser-minted journeys in priority order. `authentication` and
   * `onboarding` are checked first because they are the ones in flight while the others cannot be:
   * you cannot be authoring policy mid-signin.
   */
  function activeJourney(name?: JourneyName): Journey | null {
    if (name) return currentJourney(name)
    for (const candidate of ['authentication', 'onboarding', 'policy.author', 'marketing.acquisition'] as const) {
      const j = currentJourney(candidate)
      if (j) return j
    }
    return null
  }

  const currentJourneyBaggage = (name?: JourneyName): string | undefined => {
    const j = activeJourney(name)
    return j ? encodeJourneyBaggage(j) : undefined
  }

  /**
   * Headers for an outbound API call.
   *
   * ITS JOB IS `baggage`, and that division of labour is worth being explicit about.
   *
   * `traceparent` is injected by FetchInstrumentation, not here: it creates the fetch span and then
   * injects from the context that span is in, which is the only point at which a valid span context
   * exists. Calling this before the fetch means there is no active span yet, so the trace-context
   * propagator correctly contributes nothing.
   *
   * `baggage` is the opposite case and is why this function exists. The journey lives in
   * sessionStorage rather than in context - deliberately, because it must survive a full navigation
   * away to the IdP and back (§11) - so no propagator can discover it. It has to be attached by hand.
   *
   * It carries the journey and NOTHING else: never `user.id`, never `tenant.id`, never a token,
   * because the Worker treats baggage as attacker-controlled and derives identity from the validated
   * bearer token instead (§16.6).
   */
  function traceHeaders(journeyName?: JourneyName): Record<string, string> {
    const out: Record<string, string> = {}
    if (disabled) return out
    try {
      if (!ensureInit()) return out
      propagation.inject(context.active() ?? ROOT_CONTEXT, out, {
        set: (carrier: Record<string, string>, k: string, v: unknown) => void (carrier[k] = String(v)),
      })
      const bag = currentJourneyBaggage(journeyName)
      if (bag) out['baggage'] = bag
    } catch {
      /* a failed injection must never stop the request */
    }
    return out
  }

  // ── Emitters ────────────────────────────────────────────────────────────────────────────────────

  function trackPageView(path: string, fromPath?: string, route?: string): void {
    if (disabled || typeof window === 'undefined') return
    if (onExcludedPath()) return
    if (!ensureInit()) return
    try {
      previousPath = fromPath ?? currentPath
      currentPath = path
      // Assigned unconditionally, including to undefined: a route that failed to match must read as
      // "unknown", never as the previous page's template.
      currentRoute = route
      const from = fromPath && fromPath !== path ? fromPath : undefined
      const j = activeJourney()
      const attrs = clean({
        [ATTR.APP_PATH]: path,
        [ATTR.APP_ROUTE]: route,
        [ATTR.APP_FROM]: from,
        [ATTR.DEVICE_CLASS]: deviceClass(),
        ...identityAttrs(),
        campaign: captureCampaign(),
        ...(j ? { [ATTR.JOURNEY_ID]: j.id, [ATTR.JOURNEY_NAME]: j.name } : {}),
      })
      // The log record is kept at full fidelity and is what the marketing analysis queries; the span
      // is the thing the gateway's tail sampler may thin.
      emitLog('page_view', attrs)
      emitSpan('page_view', attrs)
    } catch {
      /* never let telemetry break navigation */
    }
  }

  function trackEvent(type: string, target?: string, opts?: { from?: string }): void {
    if (disabled || typeof window === 'undefined') return
    if (onExcludedPath()) return
    if (!ensureInit()) return
    try {
      const fromCandidate = opts?.from ?? previousPath
      const from = fromCandidate && fromCandidate !== currentPath ? fromCandidate : undefined
      const j = activeJourney()
      const attrs = clean({
        [ATTR.EVENT_TYPE]: type,
        // Comes ONLY from `data-track-target` or the element id — never href, text or value. That is
        // what keeps a mailto: address and a bot-token label out of telemetry (§10, test-enforced).
        [ATTR.EVENT_TARGET]: target,
        [ATTR.APP_PATH]: currentPath,
        [ATTR.APP_ROUTE]: currentRoute,
        [ATTR.APP_FROM]: from,
        ...identityAttrs(),
        campaign: captureCampaign(),
        ...(j ? { [ATTR.JOURNEY_ID]: j.id, [ATTR.JOURNEY_NAME]: j.name } : {}),
      })
      emitLog('ui.event', attrs)
      emitSpan('ui.event', attrs)
    } catch {
      /* never let telemetry break the page */
    }
  }

  /**
   * A span with a REAL duration, emitted RETROSPECTIVELY from a measured elapsed time.
   *
   * §8 wants `auth.bootstrap` to be a span rather than an event, because the token exchange plus up
   * to two retries of /api/auth/me with a 500/1500ms backoff is genuine duration - time-to-usable-app
   * is the number users feel. But §10 is equally clear that a span held OPEN across React effects is
   * unreliable and frequently never ended, because the data router renders asynchronously.
   *
   * Both constraints are satisfied by backdating: the caller measures with Date.now() and this emits
   * a span with explicit start and end times, exactly as the queue supervisor does for a workflow run
   * it only learns about after the fact. Nothing is held open.
   */
  function trackTiming(name: string, durationMs: number, attributes: Record<string, AttrValue | undefined> = {}): void {
    if (disabled || typeof window === 'undefined') return
    if (onExcludedPath()) return
    if (!ensureInit()) return
    try {
      const end = Date.now()
      const j = activeJourney()
      const span = getTracer().startSpan(
        name,
        {
          startTime: end - Math.max(0, durationMs),
          attributes: clean({
            ...identityAttrs(),
            [ATTR.APP_PATH]: currentPath,
            [ATTR.APP_ROUTE]: currentRoute,
            ...(j ? { [ATTR.JOURNEY_ID]: j.id, [ATTR.JOURNEY_NAME]: j.name } : {}),
            ...attributes,
          }),
        },
        ROOT_CONTEXT,
      )
      span.end(end)
    } catch {
      /* never let telemetry break the page */
    }
  }

  /**
   * Once-per-session canary.
   *
   * A steady baseline distinguishes "users stopped clicking" from "telemetry broke" — an adblocker, a
   * misconfigured endpoint, a handler bug. A log record only: no duration, nothing to parent (§8).
   */
  function trackHealthcheck(): void {
    if (disabled || typeof window === 'undefined') return
    if (onExcludedPath()) return
    const store = sessionStore()
    if (readStored(store, HC_KEY)) return
    if (!ensureInit()) return
    writeStored(store, HC_KEY, '1')
    emitLog(
      'healthcheck',
      clean({
        [ATTR.DEVICE_CLASS]: deviceClass(),
        ...identityAttrs(),
      }),
    )
  }

  /**
   * An application error: an ERROR log record plus an exception span event.
   *
   * THE ONE EXCLUDED-PATH EXCEPTION, and it is deliberate. `bootstrapError.ts` suppressed everything
   * on `/callback` and its own comment called signin-callback failures "an accepted gap" — but that is
   * the single most common place auth breaks, and it blank-screened production invisibly. With
   * `fromExcludedPath`, this emits the closed `stage` enum plus the opaque session/device/journey ids
   * and NOTHING read from `location`: no app.path, no query, no hash.
   *
   * NO STACK TRACE, ever. A minified SPA stack carries no diagnostic value without sourcemaps and is
   * pure volume (§13). And never `formatErrorDetail()` output, which embeds a response body and the
   * user agent.
   */
  function trackError(stage: BootstrapStage | string, err?: unknown, opts?: { fromExcludedPath?: boolean }): void {
    if (disabled || typeof window === 'undefined') return
    if (onExcludedPath() && !opts?.fromExcludedPath) return
    if (!ensureInit()) return
    try {
      const onExcluded = onExcludedPath()
      const j = activeJourney()
      const attrs = clean({
        [ATTR.TM_ERROR_STAGE]: stage,
        // The error's own NAME only. Not its message: a message can interpolate a response body or a
        // URL, and this is the one path that may run on /callback.
        [ATTR.EXCEPTION_TYPE]: err instanceof Error ? err.name : err !== undefined ? 'Error' : undefined,
        ...identityAttrs(),
        // Suppressed on an excluded path: app.path is read from location. app.route is config-derived
        // rather than read from the URL, but it still says which page the user was on, and the whole
        // point of the /callback exclusion is that nothing identifies that page.
        [ATTR.APP_PATH]: onExcluded ? undefined : currentPath,
        [ATTR.APP_ROUTE]: onExcluded ? undefined : currentRoute,
        ...(j ? { [ATTR.JOURNEY_ID]: j.id, [ATTR.JOURNEY_NAME]: j.name } : {}),
      })
      emitLog('bootstrap_error', attrs, SeverityNumber.ERROR)
      try {
        const span = getTracer().startSpan('app.error', { attributes: attrs }, ROOT_CONTEXT)
        span.addEvent('exception', attrs)
        span.end()
      } catch {
        /* ignore */
      }
    } catch {
      /* never let telemetry break the page */
    }
  }

  return {
    sessionId,
    deviceId,
    setUser,
    captureCampaign,
    resetTelemetryIdentity,
    trackPageView,
    trackEvent,
    trackHealthcheck,
    trackError,
    trackTiming,
    startJourney,
    endJourney,
    currentJourney,
    currentJourneyBaggage,
    traceHeaders,
    __resetPathState(): void {
      currentPath = typeof window !== 'undefined' ? window.location.pathname : ''
      previousPath = undefined
      currentRoute = undefined
    },
    /** Exposed for tests; also the hook a host could call before deliberately tearing the page down. */
    flush: flushNow,
    async __resetTelemetryForTest(): Promise<void> {
      // Remove the listeners too, or every test case leaves another pair bound to the shared jsdom
      // document and later cases flush providers that earlier cases already shut down.
      try {
        if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange)
        if (onPageHide) window.removeEventListener('pagehide', onPageHide)
      } catch {
        /* ignore */
      }
      onVisibilityChange = undefined
      onPageHide = undefined
      try {
        await tracerProvider?.shutdown()
      } catch {
        /* ignore */
      }
      try {
        await loggerProvider?.shutdown()
      } catch {
        /* ignore */
      }
      tracerProvider = undefined
      loggerProvider = undefined
      initialised = false
    },
  }
}
